import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import api from "../../../../chat/api";
import Checkbox from "../../../../../Checkbox";

const AssignMembersModal = ({
  isOpen,
  onClose,
  sectionTitle,
  sectionId,
  folderId,
  onAssignSuccess,
  assignedMembers = [], // Accepts assigned members array
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [comment, setComment] = useState("");
  const [members, setMembers] = useState([]);

  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [assignStatus, setAssignStatus] = useState("idle");

  useEffect(() => {
    if (!isOpen) return;

    setAssignStatus("idle");
    setSelectedIds([]);
    setComment("");
    setSearchTerm("");
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const normalizeMember = (m) => ({
    ...m,
    org_role: m.role,
    hasProjectAccess: m.has_project_access !== false,
    role: m.permission_role ?? m.role ?? "Member",
  });

  const fetchMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const token = Cookies.get("access_token");
      if (!token) return;

      const pathname = window.location.pathname || "";
      const match = pathname.match(/\/project\/(\d+)\/org\/(\d+)(?:\/|$)/);

      const urlFolderId = match?.[1];
      const organization_id = match?.[2];

      const resolvedFolderId = folderId ?? urlFolderId;
      if (!resolvedFolderId || !organization_id) return;

      const response = await api.get(
        `/api/v1/rfp/folders/${resolvedFolderId}/section-view/members/list/?organization_id=${organization_id}&include_org_members=true`
      );

      const list = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.results)
        ? response.data.results
        : [];

      setMembers(list.map(normalizeMember));
    } catch (error) {
      console.error("Error fetching organization members:", error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const filteredMembers = useMemo(() => {
    // 1. Filter out 'Owner' and already assigned members
    const validMembers = members.filter((m) => {
      if (m.permission_role === "Owner") return false;
      
      const isAlreadyAssigned = assignedMembers.some(
        (assigned) => assigned.user_id === m.user_id
      );
      if (isAlreadyAssigned) return false;

      return true;
    });

    // 2. People already on the project first, then apply the search filter
    const byAccessThenName = (a, b) => {
      if (a.hasProjectAccess !== b.hasProjectAccess) return a.hasProjectAccess ? -1 : 1;
      return (a.full_name || "").localeCompare(b.full_name || "");
    };

    const q = searchTerm.trim().toLowerCase();
    if (!q) return [...validMembers].sort(byAccessThenName);

    return validMembers
      .filter(
        (m) =>
          m.full_name?.toLowerCase().includes(q) ||
          m.email?.toLowerCase().includes(q)
      )
      .sort(byAccessThenName);
  }, [members, searchTerm, assignedMembers]);


  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;

    setAssignStatus("loading");
    try {
      const selectedMembersData = members.filter((m) =>
        selectedIds.includes(m.user_id)
      );
      const orgId = selectedMembersData.length > 0 ? selectedMembersData[0].organization_id : null;

      if (!orgId) {
        setAssignStatus("idle");
        return;
      }

      const payload = {
        org_id: orgId,
        section_id: sectionId,
        user_ids: selectedIds,
      };

      await api.post(
        `/api/v1/rfp/folders/${folderId}/section-view/assign-members/`,
        payload
      );

      setAssignStatus("success");

      setTimeout(() => {
        onAssignSuccess?.();
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error assigning members:", error);
      setAssignStatus("idle");
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    return name.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getButtonText = () => {
    switch (assignStatus) {
      case "loading": return "Assigning...";
      case "success": return "Assigned";
      default: return "Assign";
    }
  };

  const roleBadgeClass = (role) => {
    switch (role) {
      case "Owner": return "bg-[#EEF2FF] text-[#3730A3]";
      case "Editor": return "bg-[#ECFDF5] text-[#065F46]";
      case "Reviewer": return "bg-[#FFF2FE] text-[#BB42AE]";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  if (!isOpen) return null;
  const disableClose = assignStatus === "loading" || assignStatus === "success";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={disableClose ? undefined : onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[500px] flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-6 pb-2">
          <div>
            <h2 className="text-[#18101C] font-outfit text-xl font-semibold">Assign Members</h2>
            <p className="text-sm text-gray-500 font-outfit mt-1">{sectionTitle}</p>
          </div>
          <button
            onClick={onClose}
            disabled={disableClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="p-6 pt-2 flex flex-col gap-4 overflow-y-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search Team Members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-10 pr-4 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C95EBE] focus:border-transparent font-outfit text-sm placeholder-gray-400 leading-normal"
            />
          </div>

          <div className="border border-gray-100 rounded-xl overflow-hidden flex flex-col">
            <div className="overflow-y-auto custom-scrollbar h-[300px]">
              {isLoadingMembers ? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-[#374151] font-outfit font-medium text-sm animate-pulse">Fetching Team Members...</p>
                </div>
              ) : filteredMembers.length > 0 ? (
                filteredMembers.map((member) => {
                  const isSelected = selectedIds.includes(member.user_id);
                  return (
                    <div
                      key={member.user_id}
                      onClick={() => toggleSelection(member.user_id)}
                      className={`flex items-center p-3 gap-3 cursor-pointer transition-colors ${
                        isSelected ? "bg-[#FEF9F7]" : "hover:bg-gray-50"
                      } border-b border-gray-50 last:border-none`}
                    >
                      <div className="flex-shrink-0">
                        <Checkbox
                          id={`member-${member.user_id}`}
                          checked={isSelected}
                          onChange={(checked, e) => {
                            e?.stopPropagation?.();
                            toggleSelection(member.user_id);
                          }}
                          size="md"
                          showLabel={false}
                          selectionMode="multiple"
                        />
                      </div>
                      <div className="w-10 h-10 rounded-full bg-[#C95EBE] flex items-center justify-center !text-white font-outfit font-medium text-[15px] flex-shrink-0">
                        {getInitials(member.full_name)}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-3">
                          <span className="!text-[#18101C] font-outfit font-medium text-[14px]">
                            {member.full_name}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${roleBadgeClass(member.role)}`}>
                            {member.role || "Member"}
                          </span>
                        </div>
                        <p className="!text-[#3F3F46] font-outfit text-[12px] truncate mt-0.5">{member.email}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-8 min-h-[300px] px-4 flex flex-col items-center justify-center text-center">
                  <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                  </div>
                  <p className="text-[#374151] font-outfit font-medium text-sm">No members found</p>
                  <p className="text-[#9CA3AF] font-outfit text-xs mt-1">No unassigned members match your criteria</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block !text-[#171717] font-outfit font-medium text-[16px] mb-2">Add Comment (Optional)</label>
            <textarea
              placeholder="Add a message..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C95EBE] focus:border-transparent font-outfit text-sm placeholder-gray-400 min-h-[100px] resize-none"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={selectedIds.length === 0 || disableClose}
            className={`w-[40%] mx-auto py-3 rounded-xl font-outfit font-medium text-white transition-all duration-300 ${
              selectedIds.length > 0
                ? assignStatus === "success"
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-[#C95EBE] hover:bg-[#BB42AE] shadow-md hover:shadow-lg"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignMembersModal;
